import * as Rx from 'rxjs';
import * as RxOp from 'rxjs/operators';
import cacheService from 'services/cache';

interface IOptions {
  refresh: boolean;
  persist: boolean;
  expirationMinutes: number;
}

const pendings: { [key: string]: Rx.Observable<any> } = {};

export interface ICacheResult<T = any> {
  cached: boolean;
  updating: boolean;
  data: T;
}

export function cache<T>(key: string, options: Partial<IOptions> = {}) {
  const defaultOptions: IOptions = {
    refresh: false,
    persist: false,
    expirationMinutes: 5,
  };

  return (source: Rx.Observable<T>) =>
    source.lift<ICacheResult<T>>(new CacheOperator(key, { ...defaultOptions, ...options }));
}

class CacheOperator<T> implements Rx.Operator<T, T> {
  constructor(private key: string, private options: IOptions) {
    key = this.key;
    options = this.options;
  }

  public call(subscriber: Rx.Subscriber<any>, source: Rx.Observable<any>): Rx.Subscription {
    const start = this.options.refresh ? cacheService.removeData(this.key) : Rx.of(true);

    return start
      .pipe(
        RxOp.switchMap(() => cacheService.watchData(this.key)),
        RxOp.switchMap(cache => {
          if (cache && !cacheService.isExpirated(cache)) {
            return Rx.of({
              cached: true,
              updating: false,
              data: cache.data,
            } as ICacheResult);
          }

          if (pendings[this.key]) {
            return pendings[this.key];
          }

          const stream$ = source.pipe(
            RxOp.map(
              data =>
                ({
                  cached: false,
                  updating: true,
                  data,
                } as ICacheResult),
            ),
            RxOp.switchMap((result: ICacheResult) => {
              if (result.cached) return Rx.of(result);
              return cacheService.saveData(this.key, result.data, this.options).pipe(
                RxOp.map(() => result),
                RxOp.tap(() => delete pendings[this.key]),
              );
            }),
            RxOp.startWith({
              cached: !!(cache && cache.data),
              updating: true,
              data: cache && cache.data,
            } as ICacheResult),
            RxOp.shareReplay(1),
          );

          pendings[this.key] = stream$;

          return stream$;
        }),
        RxOp.catchError(err => {
          delete pendings[this.key];
          return Rx.throwError(err);
        }),
      )
      .subscribe(subscriber);
  }
}
